import React, { useEffect, useState } from 'react';
import {
  EventModel,
  canJoinEventMeet,
  formatLiveCountdown,
  getEventSessionStart,
} from '../../store/eventStore';
import { openGoogleMeet } from '../../utils/openGoogleMeet';

type Variant = 'hero' | 'card';

type Props = {
  event: EventModel;
  meetUrl: string;
  /** Abend-Session (linkEvening / timeEvening) */
  useEvening?: boolean;
  variant?: Variant;
  /** Zusatz im aktiven Label, z. B. "(Morgens)" */
  sessionHint?: string;
  className?: string;
};

const PlayIcon = ({ size = 20, className = '' }: { size?: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="10" />
    <polygon points="10 8 16 12 10 16 10 8" />
  </svg>
);

/**
 * Meet-Beitritt mit Phasen-Logik (upcoming / live / ended) — kein iFrame.
 */
const EventMeetJoinButton: React.FC<Props> = ({
  event,
  meetUrl,
  useEvening = false,
  variant = 'hero',
  sessionHint = '',
  className = '',
}) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const tick = () => setNow(Date.now());
    tick();
    const id = window.setInterval(tick, 15_000);
    return () => window.clearInterval(id);
  }, []);

  const url = String(meetUrl || '').trim();
  if (!url) return null;

  const start = getEventSessionStart(event, useEvening);
  const info = formatLiveCountdown(start, new Date(now));
  const kind = info?.kind || 'upcoming';
  const timeLabel = useEvening
    ? String(event.timeEvening || '').trim()
    : String(event.timeMorning || '').trim();

  const isEnded = kind === 'ended';
  /** Beitritt ab 15 Min. vor Start (nicht erst bei „LIVE“) */
  const canJoin = canJoinEventMeet(start, new Date(now));
  const disabled = !canJoin;

  let label: string;
  if (isEnded) {
    label = 'Event beendet';
  } else if (canJoin) {
    label = sessionHint ? `Jetzt beitreten ${sessionHint}` : 'Jetzt beitreten';
  } else if (timeLabel) {
    label = info?.label ? `Startet ${timeLabel} · ${info.label}` : `Startet um ${timeLabel}`;
  } else {
    label = info?.label || 'Noch nicht gestartet';
  }

  const heroBase =
    'w-full sm:w-auto px-8 py-4 rounded-xl font-bold uppercase tracking-widest transition-all flex items-center justify-center gap-3';
  const cardBase =
    'flex-1 py-3 rounded-lg font-bold uppercase tracking-widest text-[10px] transition-colors flex items-center justify-center gap-2 border';

  const activeHero = useEvening
    ? `${heroBase} bg-pe-elevated dark:bg-slate-800 hover:bg-pe-hover dark:hover:bg-slate-700 text-pe-text dark:text-white border border-pe-border dark:border-slate-700`
    : `${heroBase} bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_20px_rgba(37,99,235,0.2)]`;

  const activeCard = `${cardBase} bg-pe-elevated dark:bg-slate-800 hover:bg-pe-hover dark:hover:bg-slate-700 text-pe-text dark:text-white border-pe-border dark:border-slate-700`;

  const disabledCls =
    variant === 'hero'
      ? `${heroBase} bg-pe-elevated/60 dark:bg-slate-800/50 text-pe-muted border border-pe-border dark:border-slate-700 opacity-40 cursor-not-allowed`
      : `${cardBase} bg-pe-elevated/60 dark:bg-slate-800/50 text-pe-muted border-pe-border dark:border-slate-700 opacity-40 cursor-not-allowed`;

  const activeCls = variant === 'hero' ? activeHero : activeCard;
  const iconSize = variant === 'hero' ? 20 : 14;
  const iconTone = useEvening ? 'text-purple-400' : variant === 'card' ? 'text-blue-400' : '';

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => {
        if (!canJoin) return;
        openGoogleMeet(url);
      }}
      className={`${disabled ? disabledCls : activeCls} ${className}`}
      title={disabled ? label : 'Google Meet öffnen (ab 15 Min. vor Start)'}
    >
      <PlayIcon size={iconSize} className={disabled ? 'text-pe-muted' : iconTone} />
      <span className={variant === 'hero' && !canJoin ? 'text-xs sm:text-sm normal-case tracking-wide' : ''}>
        {label}
      </span>
    </button>
  );
};

export default EventMeetJoinButton;
